import React, { useEffect, useState } from 'react';
import { Spinner, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useDownload } from '../hooks/useDownload';

function StatoBadge({ stato }) {
  const map = {
    in_corso:    'bg-blue-100 text-blue-700',
    conclusa:    'bg-green-100 text-green-700',
    non_avviata: 'bg-gray-100 text-gray-600',
    sospesa:     'bg-yellow-100 text-yellow-700',
  };
  const cls = map[stato] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {stato.replace('_', ' ')}
    </span>
  );
}

function ScartoCell({ scarto }) {
  if (scarto === null || scarto === undefined) return <span className="text-gray-400">—</span>;
  if (scarto > 0) return <span className="text-red-600 font-semibold">+{scarto} gg</span>;
  if (scarto < 0) return <span className="text-green-600 font-semibold">{scarto} gg</span>;
  return <span className="text-green-700 text-xs">Nei termini</span>;
}

export default function ReportAvanzamento() {
  const { _locate } = useStateContext();
  const api = usePppApi();
  const { download, downloading } = useDownload();
  const [procedimenti, setProcedimenti] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProc, setLoadingProc] = useState(true);

  useEffect(() => {
    api.listProcedimenti({
      onSuccess: (data) => { setProcedimenti(data.data ?? data); setLoadingProc(false); },
      onFormError: () => setLoadingProc(false),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = () => {
    if (!selectedId) return;
    setLoading(true);
    setReport(null);
    api.getReportAvanzamento(selectedId, {
      onSuccess: (data) => { setReport(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  };

  const exportPdf = () => {
    if (!selectedId) return;
    download(api.urlReportAvanzamentoPdf(selectedId), `report_avanzamento_ppp_${selectedId}.pdf`);
  };

  const exportExcel = () => {
    if (!selectedId) return;
    download(api.urlReportAvanzamentoExcel(selectedId), `report_avanzamento_ppp_${selectedId}.csv`);
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {_locate('ppp_report_avanzamento_titolo')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {_locate('ppp_report_avanzamento_desc')}
        </p>
      </div>

      {/* Filtro procedimento */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {_locate('ppp_report_seleziona_procedimento')}
          </label>
          {loadingProc ? <Spinner /> : (
            <select
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
              value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); setReport(null); }}
            >
              <option value="">{_locate('ppp_report_scegli_procedimento')}</option>
              {procedimenti.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codice ? `${p.codice} — ` : ''}{p.titolo}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={carica}
          disabled={!selectedId || loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? _locate('ppp_report_caricamento') : _locate('ppp_report_genera')}
        </button>
        {report && (
          <>
            <button onClick={exportPdf} disabled={downloading} className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors">
              {downloading ? '...' : 'PDF'}
            </button>
            <button onClick={exportExcel} disabled={downloading} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
              {downloading ? '...' : 'Excel (CSV)'}
            </button>
          </>
        )}
      </div>

      {loading && (
        <div className="flex justify-center items-center h-40">
          <Spinner />
        </div>
      )}

      {report && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-500">{_locate('ppp_report_procedimento')}: </span><strong className="text-gray-900 dark:text-white">{report.codiceProcedimento} — {report.titoloProcedimento}</strong></div>
            <div><span className="text-gray-500">{_locate('ppp_report_stato')}: </span><strong className="text-gray-900 dark:text-white capitalize">{report.statoProcedimento}</strong></div>
            {report.rupNome && <div><span className="text-gray-500">RUP: </span><strong className="text-gray-900 dark:text-white">{report.rupNome}</strong></div>}
            <div><span className="text-gray-500">{_locate('ppp_report_generato')}: </span><span className="text-gray-600 dark:text-gray-400">{new Date(report.dataGenerazione).toLocaleString('it-IT')}</span></div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_fase')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_stato')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_data_inizio')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_data_fine')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termine_legale')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_giorni_effettivi')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_scarto')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {report.fasi.map((f) => (
                  <tr key={f.numeroFase} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                    <td className="px-3 py-3 text-gray-500 text-xs">{f.numeroFase}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{f.nomeFase}</td>
                    <td className="px-4 py-3"><StatoBadge stato={f.stato} /></td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {f.dataInizio ? new Date(f.dataInizio).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {f.dataFine ? new Date(f.dataFine).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">
                      {f.termineLeGale !== null ? `${f.termineLeGale} gg` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">
                      {f.giorniEffettivi !== null ? `${f.giorniEffettivi} gg` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ScartoCell scarto={f.scartoDalTermine} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
